import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  createSchool,
  createScheduleType,
  listScheduleTypes,
  createPeriod,
  listPeriods,
  updatePeriod as updatePeriodApi,
  deletePeriod as deletePeriodApi,
  createCalendarEntries,
  listCalendar,
  deleteCalendarEntry,
  listSchoolPeriods,
  createSchoolPeriod,
  nextDisplayOrder,
} from '../../api/schoolConfigApi';
import { showSuccess, showError } from '../../utils/notifications';
import { getContextualError, logError } from '../../utils/errorHandling';
import {
  timeToMinutes,
  getDefaultStartTime,
  getDefaultEndTime,
  findTimeOverlaps,
} from '../../utils/periodFormatHelper';
import { calendarDate } from '@hallpass/schedule';
import PassPolicyPanel from './panels/PassPolicyPanel';
import DestinationsPanel from './panels/DestinationsPanel';
import type { ScheduleType } from './panels/ScheduleConfigPanel';
import type { components } from '../../api/types.gen';
import styles from './SchoolSetupWizard.module.css';

// API/payload shapes reused from the generated OpenAPI types rather than
// hand-duplicated.
type CalendarEntry = components['schemas']['CalendarEntry'];
type SchoolPeriodResponse = components['schemas']['SchoolPeriodResponse'];

// The three schedule shapes the wizard's step-1 cards offer. 'Mixed' has no
// period inputs yet (renderScheduleStep never renders them for it), but is
// still a selectable value.
type SchoolType = 'Regular' | 'Block' | 'Mixed';

// A/B tagging on a wizard period row: 'A'/'B' for split-day periods, 'Both'
// for shared periods, null for a Regular school's undifferentiated periods.
type PeriodAbDay = 'A' | 'B' | 'Both' | null;

// One editable period row in the wizard's in-memory schedule (still free-text
// names — resolved to canonical SchoolPeriods only at save time in
// ensureScheduleTypesAndPeriods).
interface WizardPeriod {
  periodName: string;
  startTime: string;
  endTime: string;
  abDay: PeriodAbDay;
}

interface SemesterConfig {
  currentAcademicYear: number;
  fallSemesterStart: string;
  fallSemesterEnd: string;
  springSemesterStart: string;
  springSemesterEnd: string;
}

interface SetupData {
  schoolType: SchoolType;
  totalPeriods: number;
  timezone: string;
  regularSchedule: WizardPeriod[];
  semesterConfig: SemesterConfig;
}

// The ScheduleType(s) this wizard will create, grouped as they'll be persisted
// (abDay null for a Regular school's single group, 'A'/'B' for a Block
// school's two groups). abDay here matches CreateScheduleTypeBody's field.
interface ScheduleTypeGroup {
  name: string;
  abDay: 'A' | 'B' | null;
  periods: WizardPeriod[];
}

// Created ScheduleType ids keyed by abDay ('single' for the Regular school's
// null-abDay group), the shape buildSemesterCalendarEntries reads.
type ScheduleTypeIdsByAbDay = Partial<Record<'A' | 'B' | 'single', number>>;

// The completion payload handed to onSetupComplete: the collected setupData
// plus the resolved/created schoolId.
export interface SchoolSetupConfig extends SetupData {
  schoolId: number | string | null;
}

export interface SchoolSetupWizardProps {
  onSetupComplete?: (config: SchoolSetupConfig) => void;
  currentOrgId?: number | string | null;
  existingTimezone?: string | null;
}

// Name given to the schedule variant this wizard creates on first run — the
// school's base/default schedule. A Block school gets two ScheduleTypes
// sharing this same `name`, distinguished by ScheduleType.abDay ('A'/'B');
// a Regular school gets one with abDay: null — so ScheduleConfigPanel's
// groupScheduleTypes recognizes it as a single variant rather than two
// unrelated ones.
// Exported so AdminPanel.js can identify this same "base" variant when
// reconstructing schoolConfig for a returning admin (there's no isDefault
// flag on ScheduleType — this name is the only "default variant" convention
// that exists anywhere in the codebase).
export const DEFAULT_VARIANT_NAME = 'Regular';

// Common US timezones for school selection
function formatIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function addUtcDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * 24 * 60 * 60 * 1000);
}

function isWeekday(date: Date): boolean {
  const day = date.getUTCDay();
  return day !== 0 && day !== 6;
}

const SCHOOL_TYPE_OPTIONS: SchoolType[] = ['Regular', 'Block', 'Mixed'];

const TIMEZONE_OPTIONS: { value: string; label: string }[] = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'America/Phoenix', label: 'Arizona Time (MST - No DST)' },
  { value: 'America/Indianapolis', label: 'Indiana Time (ET)' },
];

const SchoolSetupWizard = ({
  onSetupComplete,
  currentOrgId,
  existingTimezone = null,
}: SchoolSetupWizardProps): React.ReactElement => {
  const { currentUser, userRole } = useAuth();
  const isSuperAdmin = userRole === 'developer'; // AuthContext's ROLE_MAP: SUPER_ADMIN -> 'developer'

  // The school this wizard configures. Every current caller (AdminPanel.js)
  // already has a school by the time it renders this wizard, so this is
  // normally non-null. It's only null for a SUPER_ADMIN who hasn't created a
  // school yet, in which case the wizard creates one as its first stage
  // (see handleComplete) using the schoolName field collected below.
  const resolvedSchoolId = currentOrgId || currentUser?.schoolId || null;
  const needsSchoolCreation = !resolvedSchoolId;

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [schoolId, setSchoolId] = useState<number | string | null>(
    resolvedSchoolId
  );
  const [schoolName, setSchoolName] = useState('');
  const [setupData, setSetupData] = useState<SetupData>(() => ({
    schoolType: 'Regular',
    totalPeriods: 8,
    timezone: 'America/Los_Angeles', // Default to Pacific Time
    regularSchedule: [],
    semesterConfig: {
      currentAcademicYear: new Date().getFullYear(), // Default to current year
      fallSemesterStart: '', // e.g., "2024-08-26"
      fallSemesterEnd: '', // e.g., "2024-12-20"
      springSemesterStart: '', // e.g., "2025-01-06"
      springSemesterEnd: '', // e.g., "2025-05-30"
    },
  }));

  // Helper function to generate default semester dates
  const generateDefaultSemesterDates = (
    academicYear: number
  ): {
    fallStart: string;
    fallEnd: string;
    springStart: string;
    springEnd: string;
  } => {
    // Generate reasonable default dates based on academic year
    const fallStart = `${academicYear}-08-26`; // August 26th
    const fallEnd = `${academicYear}-12-20`; // December 20th
    const springStart = `${academicYear + 1}-01-06`; // January 6th of following year
    const springEnd = `${academicYear + 1}-05-30`; // May 30th of following year
    return { fallStart, fallEnd, springStart, springEnd };
  };

  // Initialize default schedule based on school type and period count.
  useEffect(() => {
    generateDefaultSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setupData.schoolType, setupData.totalPeriods]);

  // Initialize default semester dates on component mount
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const defaults = generateDefaultSemesterDates(currentYear);
    setSetupData((prev) => ({
      ...prev,
      semesterConfig: {
        ...prev.semesterConfig,
        fallSemesterStart: defaults.fallStart,
        fallSemesterEnd: defaults.fallEnd,
        springSemesterStart: defaults.springStart,
        springSemesterEnd: defaults.springEnd,
      },
    }));
  }, []); // Run only once on mount

  // Seed the timezone picker from the school's persisted value once
  // AdminPanel's GET /schools/:id fetch resolves (existingTimezone starts
  // null and arrives async, after this component's initial state is
  // already set) — only relevant when this school already existed before
  // the wizard ran (e.g. a SUPER_ADMIN created it).
  useEffect(() => {
    if (existingTimezone) {
      setSetupData((prev) => ({ ...prev, timezone: existingTimezone }));
    }
  }, [existingTimezone]);

  const generateDefaultSchedule = (): void => {
    const periods: WizardPeriod[] = [];
    const { schoolType, totalPeriods } = setupData;

    if (schoolType === 'Regular') {
      // Regular school: include academic periods + Advisory/Homeroom + Lunch in total
      // Reserve 2 periods for Advisory and Lunch, use rest for academic periods
      const academicPeriods = Math.max(1, totalPeriods - 2);

      for (let i = 1; i <= academicPeriods; i++) {
        periods.push({
          periodName: `Period ${i}`,
          startTime: getDefaultStartTime(i),
          endTime: getDefaultEndTime(i),
          abDay: null,
        });
      }

      // Add Advisory/Homeroom and Lunch if there's room in total period count
      if (totalPeriods > academicPeriods) {
        periods.push({
          periodName: 'Advisory',
          startTime: '11:30',
          endTime: '11:55',
          abDay: null,
        });
      }
      if (totalPeriods > academicPeriods + 1) {
        periods.push({
          periodName: 'Lunch',
          startTime: '11:55',
          endTime: '12:25',
          abDay: null,
        });
      }
    } else if (schoolType === 'Block') {
      // Block school: split academic periods between A and B days, keep special periods shared
      // Reserve 2 periods for Advisory and Lunch, split remaining between A/B days
      const academicPeriods = Math.max(1, totalPeriods - 2);
      const halfPeriods = Math.ceil(academicPeriods / 2);

      // A Day periods
      for (let i = 1; i <= halfPeriods; i++) {
        periods.push({
          periodName: `Period ${i}`,
          startTime: getDefaultStartTime(i),
          endTime: getDefaultEndTime(i),
          abDay: 'A',
        });
      }

      // B Day periods
      for (let i = halfPeriods + 1; i <= academicPeriods; i++) {
        const blockPosition = i - halfPeriods;
        periods.push({
          periodName: `Period ${i}`,
          startTime: getDefaultStartTime(blockPosition),
          endTime: getDefaultEndTime(blockPosition),
          abDay: 'B',
        });
      }

      // Add shared periods if there's room in total period count
      if (totalPeriods > academicPeriods) {
        periods.push({
          periodName: 'Advisory',
          startTime: '11:30',
          endTime: '11:55',
          abDay: 'Both',
        });
      }
      if (totalPeriods > academicPeriods + 1) {
        periods.push({
          periodName: 'Lunch',
          startTime: '11:55',
          endTime: '12:25',
          abDay: 'Both',
        });
      }
    }

    setSetupData((prev) => ({ ...prev, regularSchedule: periods }));
  };

  const handleSchoolTypeChange = (type: SchoolType): void => {
    setSetupData((prev) => ({ ...prev, schoolType: type }));
  };

  const handlePeriodCountChange = (count: string): void => {
    const numCount = parseInt(count);
    if (numCount >= 1 && numCount <= 20) {
      setSetupData((prev) => ({ ...prev, totalPeriods: numCount }));
    }
  };

  const updatePeriod = (
    index: number,
    field: 'periodName' | 'startTime' | 'endTime',
    value: string
  ): void => {
    const updatedSchedule = [...setupData.regularSchedule];
    updatedSchedule[index] = { ...updatedSchedule[index], [field]: value };
    setSetupData((prev) => ({ ...prev, regularSchedule: updatedSchedule }));
  };

  const addPeriod = (): void => {
    const newPeriod: WizardPeriod = {
      periodName: '',
      startTime: '08:30',
      endTime: '09:30',
      abDay: setupData.schoolType === 'Block' ? 'A' : null,
    };
    setSetupData((prev) => ({
      ...prev,
      regularSchedule: [...prev.regularSchedule, newPeriod],
    }));
  };

  const addABPeriodPair = (): void => {
    // Find the next period number by looking at existing numbered periods
    const numberedPeriods = setupData.regularSchedule.filter(
      (p) => p.periodName && p.periodName.match(/^Period \d+$/)
    );

    const existingNumbers = numberedPeriods
      .map((p) => p.periodName.match(/^Period (\d+)$/))
      .filter((m): m is RegExpMatchArray => m !== null)
      .map((m) => parseInt(m[1]));

    const nextPeriodNum =
      existingNumbers.length === 0 ? 1 : Math.max(...existingNumbers) + 1;
    const periodName = `Period ${nextPeriodNum}`;

    // Create A day period
    const aPeriod: WizardPeriod = {
      periodName: periodName,
      startTime: getDefaultStartTime(nextPeriodNum),
      endTime: getDefaultEndTime(nextPeriodNum),
      abDay: 'A',
    };

    // Create B day period
    const bPeriod: WizardPeriod = {
      periodName: periodName,
      startTime: getDefaultStartTime(nextPeriodNum),
      endTime: getDefaultEndTime(nextPeriodNum),
      abDay: 'B',
    };

    setSetupData((prev) => ({
      ...prev,
      regularSchedule: [...prev.regularSchedule, aPeriod, bPeriod],
    }));
  };

  const addSharedPeriod = (): void => {
    const newPeriod: WizardPeriod = {
      periodName: '',
      startTime: '08:30',
      endTime: '09:30',
      abDay: 'Both',
    };
    setSetupData((prev) => ({
      ...prev,
      regularSchedule: [...prev.regularSchedule, newPeriod],
    }));
  };

  const removePeriod = (index: number): void => {
    const updatedSchedule = setupData.regularSchedule.filter(
      (_, i) => i !== index
    );
    setSetupData((prev) => ({ ...prev, regularSchedule: updatedSchedule }));
  };

  // Validates setupData.regularSchedule itself (names/times filled in, Block
  // schools have at least some A/B-assigned periods, no time overlaps).
  // Deliberately excludes any check that regularSchedule.length matches
  // setupData.totalPeriods: that number is only ever used to seed
  // generateDefaultSchedule's initial template, never persisted or read
  // again after this wizard closes (AdminPanel.js's schoolConfig.totalPeriods
  // is re-derived from the real Period rows on every load, not from this
  // field), and for Block schools "+ Add A/B Day Period Pair" legitimately
  // adds 2 rows for what's conceptually one more period, so raw row count
  // was never comparable to it anyway. Shared by the step-2 "Next" gate and
  // validateSetup's final check.
  const validateScheduleDetails = (): string[] => {
    const errors: string[] = [];
    const { schoolType, regularSchedule } = setupData;

    // Validate period names
    regularSchedule.forEach((period, index) => {
      if (!period.periodName)
        errors.push(`Period ${index + 1}: Name is required`);
      if (!period.startTime)
        errors.push(`Period ${index + 1}: Start time is required`);
      if (!period.endTime)
        errors.push(`Period ${index + 1}: End time is required`);
    });

    // Additional validation for numbered academic periods structure
    const numberedPeriods = regularSchedule.filter(
      (p) => p.periodName && p.periodName.match(/^Period \d+$/)
    );

    if (schoolType === 'Block') {
      const aPeriods = numberedPeriods.filter((p) => p.abDay === 'A').length;
      const bPeriods = numberedPeriods.filter((p) => p.abDay === 'B').length;
      if (aPeriods === 0 && bPeriods === 0) {
        errors.push(
          `Block schedule must have at least some periods assigned to A or B days`
        );
      }
    }

    // Check for time overlaps
    const timeErrors = validateTimeOverlaps();
    errors.push(...timeErrors);

    errors.push(...validateDuplicatePeriodNames());

    return errors;
  };

  const validateSetup = (): string[] => {
    const errors: string[] = [];
    const { schoolType, totalPeriods, regularSchedule } = setupData;

    // Validate basic requirements
    if (!schoolType) errors.push('School type is required');
    if (!totalPeriods || totalPeriods < 1)
      errors.push('Total periods must be at least 1');
    if (!regularSchedule.length) errors.push('At least one period is required');

    errors.push(...validateScheduleDetails());
    errors.push(...validateSemesterConfig());

    return errors;
  };

  const validateTimeOverlaps = (): string[] => {
    const { schoolType, regularSchedule } = setupData;

    return findTimeOverlaps(
      regularSchedule.map((period) => ({
        start: period.startTime,
        end: period.endTime,
        label: period.periodName,
        abDay: period.abDay,
      })),
      schoolType === 'Block'
    );
  };

  // Groups setupData.regularSchedule into the ScheduleType(s) this wizard
  // will create, following ScheduleVariantModal.js's saveAsNew: a Block
  // school gets an A-day and a B-day group ('Both' periods land in both),
  // anything else (Regular, and the not-yet-supported Mixed type — see
  // renderScheduleStep, which never renders period inputs for Mixed either)
  // gets one group with the whole schedule.
  const buildScheduleTypePeriodGroups = (): ScheduleTypeGroup[] => {
    const { schoolType, regularSchedule } = setupData;

    if (schoolType === 'Block') {
      return [
        {
          name: DEFAULT_VARIANT_NAME,
          abDay: 'A',
          periods: regularSchedule.filter(
            (p) => p.abDay === 'A' || p.abDay === 'Both'
          ),
        },
        {
          name: DEFAULT_VARIANT_NAME,
          abDay: 'B',
          periods: regularSchedule.filter(
            (p) => p.abDay === 'B' || p.abDay === 'Both'
          ),
        },
      ];
    }

    return [{ name: DEFAULT_VARIANT_NAME, abDay: null, periods: regularSchedule }];
  };

  // Rejects two periods sharing a name within the same ScheduleType-group.
  // ensureScheduleTypesAndPeriods' resolveSchoolPeriod resolves each period's
  // name to a canonical SchoolPeriod (schoolPeriodByName, keyed by name), so
  // two same-named periods in one group would resolve to the identical
  // schoolPeriodId and collide on Period_scheduleTypeId_schoolPeriodId_key
  // (schema.prisma) when both are createPeriod'd against that group's
  // ScheduleType -- the second call 409s.
  const validateDuplicatePeriodNames = (): string[] => {
    const errors: string[] = [];
    const groups = buildScheduleTypePeriodGroups();

    groups.forEach((group) => {
      const groupLabel = group.abDay
        ? `${group.name} — ${group.abDay} Day`
        : group.name;
      const seenNames = new Set<string>();
      group.periods.forEach((period) => {
        if (!period.periodName) return;
        if (seenNames.has(period.periodName)) {
          errors.push(
            `Duplicate period name "${period.periodName}" in ${groupLabel}: period names must be unique within the same day`
          );
        }
        seenNames.add(period.periodName);
      });
    });

    return errors;
  };

  // Creates the wizard's default schedule variant (ScheduleType rows + their
  // Period rows). Checks for already-existing rows by (name, abDay)/time
  // first, so retrying this stage after a partial failure doesn't create
  // duplicates. Returns { single/A/B: scheduleTypeId } — keyed by abDay
  // (group.abDay is null for a Regular school's one group, so that key is
  // 'single') rather than by name, since a Block school's two groups share
  // the same `name` and are distinguished only by abDay.
  const ensureScheduleTypesAndPeriods = async (
    targetSchoolId: number | string
  ): Promise<ScheduleTypeIdsByAbDay> => {
    // listScheduleTypes' generated response type omits abDay, but the endpoint
    // returns it (see ScheduleConfigPanel's ScheduleType) — widen to read it.
    const existingTypes: ScheduleType[] = (
      await listScheduleTypes(targetSchoolId)
    ).data;
    const groups = buildScheduleTypePeriodGroups();
    const scheduleTypeIdsByAbDay: ScheduleTypeIdsByAbDay = {};

    // The wizard's periods are still typed as free-text names in its own UI
    // (see renderPeriodsList) — resolved here against the school's canonical
    // SchoolPeriod registry (docs/SCHEMA_PLAN.md), creating any that don't
    // exist yet, so every createPeriod call below can carry a real
    // schoolPeriodId instead of a name.
    const schoolPeriodByName = new Map(
      (await listSchoolPeriods(targetSchoolId)).data.map(
        (sp): [string, SchoolPeriodResponse] => [sp.name, sp]
      )
    );
    const resolveSchoolPeriod = async (
      name: string
    ): Promise<SchoolPeriodResponse> => {
      let schoolPeriod = schoolPeriodByName.get(name);
      if (!schoolPeriod) {
        const displayOrder = nextDisplayOrder([...schoolPeriodByName.values()]);
        schoolPeriod = await createSchoolPeriod(targetSchoolId, { name, displayOrder });
        schoolPeriodByName.set(schoolPeriod.name, schoolPeriod);
      }
      return schoolPeriod;
    };

    for (const group of groups) {
      let scheduleType: ScheduleType | undefined = existingTypes.find(
        (t) => t.name === group.name && t.abDay === group.abDay
      );
      if (!scheduleType) {
        scheduleType = await createScheduleType(targetSchoolId, {
          name: group.name,
          abDay: group.abDay,
        });
      }
      scheduleTypeIdsByAbDay[group.abDay ?? 'single'] = scheduleType.id;

      const existingPeriods = (
        await listPeriods(targetSchoolId, scheduleType.id)
      ).data;
      const sortedPeriods = [...group.periods].sort(
        (a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime)
      );

      // Working copy of existingPeriods: each entry is matched (consumed)
      // against at most one sortedPeriods entry below. A plain array +
      // splice is used instead of Array.find() because the match key
      // (period.periodName) is not statically guaranteed unique --
      // validateDuplicatePeriodNames (see validateScheduleDetails) now
      // rejects two periods sharing a name within the same day-group before
      // handleComplete ever reaches this function, since resolveSchoolPeriod
      // below would otherwise resolve same-named periods to the identical
      // schoolPeriodId and collide with Period_scheduleTypeId_
      // schoolPeriodId_key (schema.prisma) when both are created against the
      // same ScheduleType. The splice-based consumption is kept anyway as a
      // defensive guard against any lookup collision: without it, a second
      // same-named sortedPeriods entry would re-match the same
      // existingPeriod as the first (Array.find always returns the first
      // hit), causing either a wrong updatePeriodApi call (writing the
      // second period's times onto the first period's row) or the second
      // period being silently dropped (never created, never updated).
      const remainingExistingPeriods = [...existingPeriods];

      for (let i = 0; i < sortedPeriods.length; i++) {
        const period = sortedPeriods[i];
        const matchIndex = remainingExistingPeriods.findIndex(
          (p) => p.name === period.periodName
        );

        if (matchIndex === -1) {
          const schoolPeriod = await resolveSchoolPeriod(period.periodName);
          await createPeriod(targetSchoolId, scheduleType.id, {
            schoolPeriodId: schoolPeriod.id,
            startTime: period.startTime,
            endTime: period.endTime,
            order: i + 1,
          });
        } else {
          const [existingPeriod] = remainingExistingPeriods.splice(
            matchIndex,
            1
          );
          if (
            existingPeriod.startTime !== period.startTime ||
            existingPeriod.endTime !== period.endTime ||
            existingPeriod.order !== i + 1
          ) {
            await updatePeriodApi(
              targetSchoolId,
              scheduleType.id,
              existingPeriod.id,
              {
                startTime: period.startTime,
                endTime: period.endTime,
                order: i + 1,
              }
            );
          }
        }
      }

      // Any existingPeriods entries left unconsumed after the pass above existed
      // before this run but have no corresponding entry in the wizard's current
      // period list (edit-mode removal) -- delete them. In create mode (or a
      // retry after partial failure) every existingPeriods entry gets consumed by
      // the loop above, so remainingExistingPeriods is empty and this is a no-op.
      for (const existingPeriod of remainingExistingPeriods) {
        await deletePeriodApi(
          targetSchoolId,
          scheduleType.id,
          existingPeriod.id
        );
      }
    }

    return scheduleTypeIdsByAbDay;
  };

  // Generates real SchoolCalendar entries so the school has a working
  // schedule immediately after setup, instead of only recording 4 note-only
  // "no school" markers for the semester boundary dates (see git history —
  // that was the previous, buggy behavior: those markers set
  // scheduleTypeId: null, which the resolver/route/UI all treat identically
  // to an actual holiday, per packages/schedule/tests/resolver.test.ts).
  // Every weekday (Mon-Fri) in each semester range gets an entry; weekends
  // and the winter gap between semesters correctly get none, falling
  // through to "no school" (same resolver contract). For a Block school,
  // A/B day strictly alternates across weekdays only, anchored so the first
  // weekday in each range is an A Day — a starting point, not meant to
  // survive real-world holidays. ScheduleConfigPanel's CSV import is how an
  // admin corrects specific dates afterward.
  const buildSemesterCalendarEntries = (
    scheduleTypeIdsByAbDay: ScheduleTypeIdsByAbDay
  ): CalendarEntry[] => {
    const { schoolType, semesterConfig } = setupData;
    const isBlock = schoolType === 'Block';
    const entries: CalendarEntry[] = [];

    const semesterRanges: [string, string][] = [
      [semesterConfig.fallSemesterStart, semesterConfig.fallSemesterEnd],
      [semesterConfig.springSemesterStart, semesterConfig.springSemesterEnd],
    ];
    const ranges = semesterRanges.filter(([start, end]) => start && end);

    for (const [startStr, endStr] of ranges) {
      const end = calendarDate(endStr);
      let cursor = calendarDate(startStr);
      let abDayToggle: 'A' | 'B' = 'A';

      while (cursor <= end) {
        if (isWeekday(cursor)) {
          const scheduleTypeId = isBlock
            ? scheduleTypeIdsByAbDay[abDayToggle]
            : scheduleTypeIdsByAbDay.single;

          if (scheduleTypeId) {
            entries.push({
              date: formatIsoDate(cursor),
              scheduleTypeId,
              note: null,
            });
          }

          if (isBlock) {
            abDayToggle = abDayToggle === 'A' ? 'B' : 'A';
          }
        }

        cursor = addUtcDays(cursor, 1);
      }
    }

    return entries;
  };

  const handleComplete = async (): Promise<void> => {
    // validateScheduleDetails' checks (names/times, Block A/B assignment,
    // time overlaps) already gate step 2's "Next" button, so this is a
    // defensive final check, not expected to ever fail in normal navigation.
    const validationErrors = validateSetup();

    if (validationErrors.length > 0) {
      console.error(
        'Critical validation errors preventing setup completion:',
        validationErrors
      );
      showError(`Setup validation failed:\n${validationErrors.join('\n')}`);
      return;
    }
    console.log('Setup validation passed successfully');

    if (needsSchoolCreation && !isSuperAdmin) {
      showError(
        'No school is set up for your account yet. Please contact a system administrator to create your school before continuing setup.'
      );
      return;
    }
    if (needsSchoolCreation && !schoolName.trim()) {
      showError('School name is required to create a new school.');
      return;
    }

    setLoading(true);
    let currentStage = 'school';
    try {
      // Stage 1: create the school itself (SUPER_ADMIN only, and only when
      // no school exists yet — every other case reuses resolvedSchoolId).
      let targetSchoolId = schoolId;
      if (!targetSchoolId) {
        const created = await createSchool({
          name: schoolName.trim(),
          timezone: setupData.timezone,
        });
        targetSchoolId = created.id;
        setSchoolId(targetSchoolId);
      }

      // Stage 2: schedule types + periods for the default schedule variant.
      currentStage = 'schedule';
      const scheduleTypeIdsByAbDay =
        await ensureScheduleTypesAndPeriods(targetSchoolId);

      // Stage 3: generate a real weekday calendar for both semesters.
      currentStage = 'calendar';
      const calendarEntries = buildSemesterCalendarEntries(
        scheduleTypeIdsByAbDay
      );

      // The bulk calendar POST upserts on (schoolId, date) and never deletes.
      // If the admin went Back and narrowed the semester range before
      // continuing again, the weekday entries this wizard generated for the
      // old, wider range would otherwise linger outside the corrected range.
      // Remove those stale weekday entries first, via the existing per-entry
      // DELETE, before re-seeding the current range. Dates are compared as
      // "YYYY-MM-DD" (calendarEntries already use that form; a GET entry may
      // carry a full ISO timestamp, so it's sliced back to the date).
      const desiredDates = new Set(calendarEntries.map((entry) => entry.date));
      const existingCalendar =
        (await listCalendar(targetSchoolId))?.data ?? [];
      const staleWeekdayEntries = existingCalendar.filter((entry) => {
        const isoDate = String(entry.date).slice(0, 10);
        return isWeekday(calendarDate(isoDate)) && !desiredDates.has(isoDate);
      });
      for (const entry of staleWeekdayEntries) {
        await deleteCalendarEntry(targetSchoolId, entry.id);
      }

      if (calendarEntries.length > 0) {
        await createCalendarEntries(targetSchoolId, calendarEntries);
      }

      // School/schedule/calendar are saved — hand off to the Pass Policy and
      // Destinations steps below (steps 4 and 5), which embed
      // PassPolicyPanel/DestinationsPanel directly and persist their own
      // changes.
      setStep(4);
    } catch (error) {
      logError(error, `SchoolSetupWizard:${currentStage}`);
      showError(
        getContextualError(
          error,
          undefined,
          `Failed to complete school setup (${currentStage} step). You can try again.`
        )
      );
    } finally {
      setLoading(false);
    }
  };

  // Final step. PassPolicyPanel and DestinationsPanel (steps 4 and 5) save
  // their own changes directly via their own Save/Add buttons, so there's
  // nothing left for the wizard to submit here — just the completion signal.
  const handleFinish = (): void => {
    showSuccess('School setup completed successfully!');

    const completedConfig: SchoolSetupConfig = { schoolId, ...setupData };
    if (onSetupComplete) {
      onSetupComplete(completedConfig);
    }
  };

  const renderStepIndicator = (): React.ReactElement => (
    <div className={styles['setup-progress']}>
      <div
        className={`${styles['step']} ${step >= 1 ? styles['active'] : ''}`}
      >
        <span>1</span>
        <label>School Type</label>
      </div>
      <div
        className={`${styles['step']} ${step >= 2 ? styles['active'] : ''}`}
      >
        <span>2</span>
        <label>Schedule Configuration</label>
      </div>
      <div
        className={`${styles['step']} ${step >= 3 ? styles['active'] : ''}`}
      >
        <span>3</span>
        <label>Semester Configuration</label>
      </div>
      <div
        className={`${styles['step']} ${step >= 4 ? styles['active'] : ''}`}
      >
        <span>4</span>
        <label>Pass Policy</label>
      </div>
      <div
        className={`${styles['step']} ${step >= 5 ? styles['active'] : ''}`}
      >
        <span>5</span>
        <label>Destinations</label>
      </div>
    </div>
  );

  const renderSchoolTypeStep = (): React.ReactElement => (
    <div className="setup-step">
      <h3>Select Your School's Schedule Type</h3>
      <p>
        This determines how periods are organized and scheduled throughout the
        week.
      </p>

      {needsSchoolCreation && (
        <div className="school-name-section">
          <label>
            School Name:
            <input
              type="text"
              value={schoolName}
              onChange={(e) => setSchoolName(e.target.value)}
              className="school-name-input"
            />
          </label>
          <small>This creates a new school record for your account.</small>
        </div>
      )}

      <div className={styles['school-type-options']}>
        {SCHOOL_TYPE_OPTIONS.map((type) => (
          <div
            key={type}
            className={`${styles['school-type-card']} ${
              setupData.schoolType === type ? styles['selected'] : ''
            }`}
            onClick={() => handleSchoolTypeChange(type)}
          >
            <h4>{type} Schedule</h4>
            <p>
              {type === 'Regular' &&
                'All periods meet every day in the same order'}
              {type === 'Block' && 'Periods alternate between A and B days'}
              {type === 'Mixed' && 'Combination of regular and block schedules'}
            </p>
          </div>
        ))}
      </div>

      <div className={styles['period-count-section']}>
        <label>
          Total Number of Periods in a Complete School Day:
          <input
            type="number"
            min="1"
            max="20"
            value={setupData.totalPeriods}
            onChange={(e) => handlePeriodCountChange(e.target.value)}
            className={styles['period-count-input']}
          />
        </label>
        <small>
          This should include all periods: academic periods + Advisory/Homeroom
          + Lunch + any other scheduled blocks
        </small>
      </div>

      <div className={styles['timezone-section']}>
        <label>
          School Time Zone:
          <select
            value={setupData.timezone}
            onChange={(e) =>
              setSetupData((prev) => ({ ...prev, timezone: e.target.value }))
            }
            className={styles['timezone-select']}
          >
            {TIMEZONE_OPTIONS.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
        </label>
        <small>
          Select the time zone where your school is located. This affects when
          periods start and end.
        </small>
      </div>
    </div>
  );

  const renderScheduleStep = (): React.ReactElement => (
    <div className="setup-step">
      <h3>Configure Your Regular Schedule</h3>
      <p>
        Set up the default schedule that will be used as the base for your
        school. This includes all periods: academic classes, Advisory/Homeroom,
        Lunch, and any other scheduled blocks.
      </p>

      <p className={styles['period-count-summary']}>
        {setupData.regularSchedule.length}{' '}
        {setupData.regularSchedule.length === 1 ? 'period' : 'periods'}{' '}
        currently configured
      </p>

      <div className={styles['schedule-form-container']}>
        {setupData.schoolType === 'Block' && (
          <div className={styles['block-schedule-layout']}>
            <div className={styles['schedule-column']}>
              <h4>A Day Periods</h4>
              {renderPeriodsList('A')}
            </div>
            <div className={styles['schedule-column']}>
              <h4>B Day Periods</h4>
              {renderPeriodsList('B')}
            </div>
            <div className={styles['schedule-column']}>
              <h4>Shared Periods</h4>
              {renderPeriodsList('Both')}
            </div>
          </div>
        )}

        {setupData.schoolType === 'Regular' && (
          <div className={styles['regular-schedule-layout']}>
            <h4>Daily Schedule</h4>
            {renderPeriodsList(null)}
          </div>
        )}
      </div>

      {setupData.schoolType === 'Block' && (
        <div className={styles['add-period-buttons']}>
          <button onClick={addABPeriodPair} className={styles['add-period-btn']}>
            + Add A/B Day Period Pair
          </button>
          <button
            onClick={addSharedPeriod}
            className={`${styles['add-period-btn']} ${styles['add-shared-btn']}`}
          >
            + Add Shared Period
          </button>
        </div>
      )}

      {setupData.schoolType === 'Regular' && (
        <button onClick={addPeriod} className={styles['add-period-btn']}>
          + Add Period
        </button>
      )}
    </div>
  );

  const renderPeriodsList = (
    abDayFilter: PeriodAbDay
  ): React.ReactElement => {
    const filteredPeriods = setupData.regularSchedule.filter((period) => {
      if (abDayFilter === null) return !period.abDay;
      return period.abDay === abDayFilter;
    });

    if (filteredPeriods.length === 0) {
      return (
        <div className={styles['periods-list']}>
          <p className={styles['no-periods-message']}>
            No periods added yet. Click "Add Period" to get started.
          </p>
        </div>
      );
    }

    return (
      <div className={styles['periods-list']}>
        <div className={styles['period-form-header']}>
          <span className={styles['header-period-name']}>Period Name</span>
          <span>Start Time</span>
          <span>End Time</span>
          <span>Remove</span>
        </div>
        {filteredPeriods.map((period, index) => {
          const actualIndex = setupData.regularSchedule.indexOf(period);
          const isSpecialPeriod =
            period.periodName === 'Advisory' ||
            period.periodName === 'Lunch' ||
            period.abDay === 'Both';

          // The column headers above are decorative <span>s, not real labels,
          // so give each control its own accessible name. Where the period
          // already has a name, work it into the time/remove labels so screen
          // readers can tell otherwise-identical rows apart.
          const rowLabel = period.periodName
            ? `"${period.periodName}"`
            : `period ${actualIndex + 1}`;

          return (
            <div
              key={actualIndex}
              className={`${styles['period-form-row']} ${
                isSpecialPeriod ? styles['special-period'] : ''
              }`}
            >
              <input
                type="text"
                placeholder="Period name"
                aria-label={`Name for ${rowLabel}`}
                value={period.periodName}
                onChange={(e) =>
                  updatePeriod(actualIndex, 'periodName', e.target.value)
                }
                className={styles['period-name-input']}
              />

              <input
                type="time"
                aria-label={`Start time for ${rowLabel}`}
                value={period.startTime}
                onChange={(e) =>
                  updatePeriod(actualIndex, 'startTime', e.target.value)
                }
                className={styles['time-input']}
              />

              <input
                type="time"
                aria-label={`End time for ${rowLabel}`}
                value={period.endTime}
                onChange={(e) =>
                  updatePeriod(actualIndex, 'endTime', e.target.value)
                }
                className={styles['time-input']}
              />

              <button
                onClick={() => removePeriod(actualIndex)}
                className={styles['remove-period-btn']}
                aria-label={`Remove ${rowLabel}`}
                title="Remove period"
              >
                ×
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  // Validation functions for step navigation
  const isScheduleValid = (): boolean => {
    // Check if there are periods configured
    return Boolean(
      setupData.regularSchedule && setupData.regularSchedule.length > 0
    );
  };

  // Runs the period-level validation (names/times, Block A/B assignment,
  // time overlaps) before leaving the period-setup step, instead of only
  // surfacing it at "Complete Setup" on the final step after the admin has
  // also filled out semester dates.
  const handleNext = (): void => {
    if (step === 2) {
      const scheduleErrors = validateScheduleDetails();
      if (scheduleErrors.length > 0) {
        showError(
          `Please fix the following before continuing:\n${scheduleErrors.join('\n')}`
        );
        return;
      }
    }
    setStep(step + 1);
  };

  // Defensive final check mirroring validateScheduleDetails/validateSetup:
  // isSemesterConfigValid already gates step 3's 'Continue' button (including
  // the start<=end check below), so this should never fire in normal
  // navigation — but it stops buildSemesterCalendarEntries from silently
  // producing zero calendar entries for a semester if a bad range ever
  // reaches handleComplete.
  const validateSemesterConfig = (): string[] => {
    const errors: string[] = [];
    const { semesterConfig } = setupData;

    if (
      semesterConfig.fallSemesterStart &&
      semesterConfig.fallSemesterEnd &&
      semesterConfig.fallSemesterStart > semesterConfig.fallSemesterEnd
    ) {
      errors.push('Fall semester start date must be before its end date');
    }
    if (
      semesterConfig.springSemesterStart &&
      semesterConfig.springSemesterEnd &&
      semesterConfig.springSemesterStart > semesterConfig.springSemesterEnd
    ) {
      errors.push('Spring semester start date must be before its end date');
    }

    return errors;
  };

  const isSemesterConfigValid = (): boolean => {
    const { semesterConfig } = setupData;
    return Boolean(
      semesterConfig.currentAcademicYear &&
      semesterConfig.fallSemesterStart &&
      semesterConfig.fallSemesterEnd &&
      semesterConfig.springSemesterStart &&
      semesterConfig.springSemesterEnd &&
      isValidDate(semesterConfig.fallSemesterStart) &&
      isValidDate(semesterConfig.fallSemesterEnd) &&
      isValidDate(semesterConfig.springSemesterStart) &&
      isValidDate(semesterConfig.springSemesterEnd) &&
      semesterConfig.fallSemesterStart <= semesterConfig.fallSemesterEnd &&
      semesterConfig.springSemesterStart <= semesterConfig.springSemesterEnd
    );
  };

  const isValidDate = (dateString: string): boolean => {
    if (!dateString) return false;
    const date = new Date(dateString);
    return Boolean(
      date instanceof Date &&
        !isNaN(date.getTime()) &&
        dateString.match(/^\d{4}-\d{2}-\d{2}$/)
    );
  };

  const handleAcademicYearChange = (year: string): void => {
    const defaults = generateDefaultSemesterDates(parseInt(year));
    setSetupData((prev) => ({
      ...prev,
      semesterConfig: {
        ...prev.semesterConfig,
        currentAcademicYear: parseInt(year),
        fallSemesterStart: defaults.fallStart,
        fallSemesterEnd: defaults.fallEnd,
        springSemesterStart: defaults.springStart,
        springSemesterEnd: defaults.springEnd,
      },
    }));
  };

  const updateSemesterConfig = (
    field:
      | 'fallSemesterStart'
      | 'fallSemesterEnd'
      | 'springSemesterStart'
      | 'springSemesterEnd',
    value: string
  ): void => {
    setSetupData((prev) => ({
      ...prev,
      semesterConfig: {
        ...prev.semesterConfig,
        [field]: value,
      },
    }));
  };

  const renderSemesterStep = (): React.ReactElement => (
    <div className="setup-step">
      <h3>Configure Academic Calendar</h3>
      <p>
        Set up semester end dates to help track pass usage across academic
        years.
      </p>

      <div className={styles['semester-config-section']}>
        <div className={styles['academic-year-section']}>
          <label>
            Current Academic Year:
            <select
              value={setupData.semesterConfig.currentAcademicYear}
              onChange={(e) => handleAcademicYearChange(e.target.value)}
              className={styles['academic-year-select']}
            >
              {/* Generate options for current year and next few years */}
              {Array.from({ length: 5 }, (_, i) => {
                const year = new Date().getFullYear() + i - 1;
                return (
                  <option key={year} value={year}>
                    {year}-{year + 1}
                  </option>
                );
              })}
            </select>
          </label>
          <small>
            Academic year when school starts (e.g., 2024-2025 school year starts
            in fall 2024)
          </small>
        </div>

        <div className={styles['semester-dates-section']}>
          <div className="semester-row">
            <div className={styles['semester-date-group']}>
              <label>
                Fall Semester Start Date:
                <input
                  type="date"
                  value={setupData.semesterConfig.fallSemesterStart}
                  onChange={(e) =>
                    updateSemesterConfig('fallSemesterStart', e.target.value)
                  }
                  className={styles['semester-date-input']}
                />
              </label>
              <small>When fall semester begins</small>
            </div>

            <div className={styles['semester-date-group']}>
              <label>
                Fall Semester End Date:
                <input
                  type="date"
                  value={setupData.semesterConfig.fallSemesterEnd}
                  onChange={(e) =>
                    updateSemesterConfig('fallSemesterEnd', e.target.value)
                  }
                  className={styles['semester-date-input']}
                />
              </label>
              <small>When fall semester ends</small>
            </div>
          </div>

          <div className="semester-row">
            <div className={styles['semester-date-group']}>
              <label>
                Spring Semester Start Date:
                <input
                  type="date"
                  value={setupData.semesterConfig.springSemesterStart}
                  onChange={(e) =>
                    updateSemesterConfig('springSemesterStart', e.target.value)
                  }
                  className={styles['semester-date-input']}
                />
              </label>
              <small>When spring semester begins</small>
            </div>

            <div className={styles['semester-date-group']}>
              <label>
                Spring Semester End Date:
                <input
                  type="date"
                  value={setupData.semesterConfig.springSemesterEnd}
                  onChange={(e) =>
                    updateSemesterConfig('springSemesterEnd', e.target.value)
                  }
                  className={styles['semester-date-input']}
                />
              </label>
              <small>When spring semester ends</small>
            </div>
          </div>
        </div>

        <div className={styles['semester-info-section']}>
          <h4>How This Helps</h4>
          <ul>
            <li>
              <strong>Historical Tracking:</strong> Separate pass usage between
              semesters
            </li>
            <li>
              <strong>Semester Limits:</strong> Set different pass limits per
              semester
            </li>
            <li>
              <strong>Reporting:</strong> Generate reports by semester and
              academic year
            </li>
            <li>
              <strong>Data Management:</strong> Archive old semester data
              efficiently
            </li>
          </ul>
        </div>
      </div>
    </div>
  );

  const renderPolicyStep = (): React.ReactElement => (
    <div className="setup-step">
      <h3>Configure Pass Policy</h3>
      <p>
        Set school-wide pass limits. You can revisit this anytime from the admin
        dashboard.
      </p>
      {/* schoolId is always set by the time step 4 renders (handleComplete
          resolves/creates it first); `?? undefined` only narrows null away for
          the panel's optional prop — it falls back to currentUser either way. */}
      <PassPolicyPanel schoolId={schoolId ?? undefined} embedded />
    </div>
  );

  const renderDestinationsStep = (): React.ReactElement => (
    <div className="setup-step">
      <h3>Configure Destinations</h3>
      <p>
        Add the destinations students can request passes for. You can revisit
        this anytime from the admin dashboard.
      </p>
      {/* schoolId is always set by the time step 5 renders (handleComplete
          resolves/creates it before advancing past step 3); DestinationsPanel
          requires a non-null schoolId, so guard the always-true narrowing. */}
      {schoolId != null && <DestinationsPanel schoolId={schoolId} embedded />}
    </div>
  );

  return (
    <div className={styles['school-setup-wizard']}>
      <div className={styles['setup-header']}>
        <h2>School Setup Wizard</h2>
        <p>Let's configure your school's schedule system</p>
        {renderStepIndicator()}
      </div>

      <div className="setup-content">
        {step === 1 && renderSchoolTypeStep()}
        {step === 2 && renderScheduleStep()}
        {step === 3 && renderSemesterStep()}
        {step === 4 && renderPolicyStep()}
        {step === 5 && renderDestinationsStep()}
      </div>

      <div className={styles['setup-actions']}>
        {step > 1 && (
          <button
            onClick={() => setStep(step - 1)}
            className={styles['btn-secondary']}
            disabled={loading}
          >
            Previous
          </button>
        )}

        {step < 3 && (
          <button
            onClick={handleNext}
            className={styles['btn-primary']}
            disabled={
              (step === 1 &&
                (!setupData.schoolType ||
                  !setupData.totalPeriods ||
                  !setupData.timezone ||
                  (needsSchoolCreation && !schoolName.trim()))) ||
              (step === 2 && !isScheduleValid())
            }
          >
            Next
          </button>
        )}

        {step === 3 && (
          <button
            onClick={handleComplete}
            className={styles['btn-primary']}
            disabled={loading || !isSemesterConfigValid()}
          >
            {loading ? 'Saving...' : 'Continue'}
          </button>
        )}

        {step === 4 && (
          <button onClick={() => setStep(5)} className={styles['btn-primary']}>
            Continue
          </button>
        )}

        {step === 5 && (
          <button onClick={handleFinish} className={styles['btn-primary']}>
            Finish Setup
          </button>
        )}
      </div>
    </div>
  );
};

export default SchoolSetupWizard;
